// src/pages/CuratorLog.tsx
//
// Memory log: a rolling, live-updating view of curator decisions.
// Lets the user (and us, during development and audit) see what
// the AI is thinking when it considers a chat turn, photo, or
// heartbeat — what it remembered, what it discarded, and why.
//
// Phase 3C scope (intentionally minimal per Daniel's call):
//   - Reverse-chronological feed of curator decisions
//   - Live updates via Firestore snapshot listener
//   - Per-decision card with at-a-glance status + expandable
//     details
//   - "Load older" pagination
//
// Out of scope here, may come later:
//   - Memory atom detail view (tap a decision → see the resulting
//     memory's full evidence chain, retrieval history, outcomes)
//   - Forget / Confirm card-level actions
//   - Memory Q&A / chat with curator (deliberately excluded —
//     would compromise provenance discipline; see chat
//     conversation re: hall-of-mirrors risk)
//   - 90-day rollup summarization

import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Psychology } from '@mui/icons-material';
import { useCuratorDecisions } from '../hooks/useCuratorDecisions';
import { CuratorDecisionCard } from '../components/curator/CuratorDecisionCard';
import { useT } from '../i18n/useT';

export const CuratorLog: React.FC = () => {
  const theme = useTheme();
  const t = useT();
  const { decisions, isLoading, error, loadMore, pageSize } =
    useCuratorDecisions(50);

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: alpha(theme.palette.background.default, 1),
      }}
    >
      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
          p: { xs: 2, sm: 3 },
        }}
      >
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <Psychology sx={{ color: 'text.primary', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('Memory log')}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {t("A live record of what the AI is learning about you and how it's deciding. Each entry is a moment where the curator considered new evidence — chat turns, photos you take, places you visit — and decided whether to remember it.")}
        </Typography>

        {/* States */}
        {isLoading && decisions.length === 0 && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ py: 8, color: 'text.secondary' }}
          >
            <CircularProgress size={28} sx={{ mb: 2 }} />
            <Typography variant="body2">{t('Loading…')}</Typography>
          </Stack>
        )}

        {error && !isLoading && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.error.main}`,
              borderRadius: 1,
              p: 2,
              backgroundColor: alpha(theme.palette.error.main, 0.05),
            }}
          >
            <Typography variant="body2" color="error">
              {t("Couldn't load the memory log:")} {error}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && decisions.length === 0 && (
          <Box
            sx={{
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {t('Nothing yet.')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('The AI builds memory as you use the app. Send a chat message, take a photo, or explore a place — and decisions will start appearing here.')}
            </Typography>
          </Box>
        )}

        {/* Feed */}
        {decisions.length > 0 && (
          <Stack spacing={1.5}>
            {decisions.map((d) => (
              <CuratorDecisionCard key={d.id} decision={d} />
            ))}
          </Stack>
        )}

        {/* Load older — appears when we have a full page (might be
            more behind it). Bumps the pageSize, which causes the
            snapshot subscription to re-issue with a higher limit. */}
        {decisions.length >= pageSize && !isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button onClick={loadMore} variant="outlined" size="small">
              {t('Load older')}
            </Button>
          </Box>
        )}

        {/* Footnote — small explanatory note about the colors and
            statuses. Helps make the log self-explanatory. */}
        {decisions.length > 0 && (
          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              color: 'text.secondary',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              {t('How to read this:')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6 }}>
              <b>{t('create')}</b> {t('· the AI wrote a new memory.')}{' '}
              <b>{t('merge')}</b> {t('· folded into something it already knew.')}{' '}
              <b>{t('supersede')}</b> {t('· replaced an outdated memory with new info.')}{' '}
              <b>{t('annotate')}</b> {t('· confirmed something it already knew.')}{' '}
              <b>{t('discard')}</b> {t("· decided not to remember (most decisions land here — that's intentional).")}{' '}
              <b>{t('promote pending')}</b> {t('· marked tentative; will commit on more evidence.')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
